import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Conditionally injects the admin-only PWA manifest + iOS install meta tags
 * into <head> when the user is on an /admin* route. Removes them otherwise so
 * the public marketing site is never advertised as installable.
 */
export function AdminPWAManifest() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    const head = document.head;
    const ids = {
      manifest: "admin-pwa-manifest",
      themeColor: "admin-pwa-theme",
      appleCapable: "admin-pwa-apple-capable",
      appleStatus: "admin-pwa-apple-status",
      appleTitle: "admin-pwa-apple-title",
      appleIcon: "admin-pwa-apple-icon",
    };

    const remove = () => Object.values(ids).forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.remove();
    });

    // Public manifest link in index.html — disable while on admin so the
    // admin manifest wins, and restore it when leaving admin.
    const publicManifest = document.querySelector<HTMLLinkElement>(
      'link[rel="manifest"]:not(#admin-pwa-manifest)'
    );

    if (!isAdmin) {
      remove();
      if (publicManifest) publicManifest.rel = "manifest";
      return;
    }

    remove();
    if (publicManifest) publicManifest.rel = "manifest-disabled";

    const manifest = document.createElement("link");
    manifest.id = ids.manifest;
    manifest.rel = "manifest";
    manifest.href = "/admin-manifest.webmanifest";
    head.appendChild(manifest);

    const theme = document.createElement("meta");
    theme.id = ids.themeColor;
    theme.name = "theme-color";
    theme.content = "#0a1628";
    head.appendChild(theme);

    const appleCap = document.createElement("meta");
    appleCap.id = ids.appleCapable;
    appleCap.name = "apple-mobile-web-app-capable";
    appleCap.content = "yes";
    head.appendChild(appleCap);

    const appleStatus = document.createElement("meta");
    appleStatus.id = ids.appleStatus;
    appleStatus.name = "apple-mobile-web-app-status-bar-style";
    appleStatus.content = "black-translucent";
    head.appendChild(appleStatus);

    const appleTitle = document.createElement("meta");
    appleTitle.id = ids.appleTitle;
    appleTitle.name = "apple-mobile-web-app-title";
    appleTitle.content = "TPP Admin";
    head.appendChild(appleTitle);

    const appleIcon = document.createElement("link");
    appleIcon.id = ids.appleIcon;
    appleIcon.rel = "apple-touch-icon";
    appleIcon.href = "/admin-apple-touch-icon.png";
    head.appendChild(appleIcon);

    return () => {
      remove();
      if (publicManifest) publicManifest.rel = "manifest";
    };
  }, [isAdmin]);

  return null;
}